import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeDto, EmployeeCreateDto } from '@personnel/application/dtos/EmployeeResponseDto';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Hook for creating a new employee
 *
 * Uses React Query mutation to handle the employee creation flow.
 * Automatically invalidates the employees list cache on success.
 * The closing toast is the caller's: its wording depends on the save that was asked for.
 *
 * @returns {UseMutationResult} React Query mutation result
 *
 * @example
 * const { mutateAsync: createEmployee } = useCreateEmployee();
 *
 * try {
 *   await createEmployee({ ... });
 * } catch (error) {
 *   // Handle error (show toast, set form field error, etc.)
 * }
 */
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { create } = getEmployeeUseCases();

  return useMutation<EmployeeDto, Error, EmployeeCreateDto>(
    async (dto: EmployeeCreateDto) => {
      return create.execute(dto);
    },
    {
      onSuccess: () => {
        // Invalidate employees list to refresh the table
        queryClient.invalidateQueries(['personnel', 'employees', 'list']);
        // Invalidate roles list to refresh usersCount
        queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

        history.push('/employees');
      },
    },
  );
};
